import React, { useEffect, useRef, useState } from 'react'
import axiosInstance from '../../api/axiosInstance';
import { RiVoiceAiLine } from "react-icons/ri";
import { IoCloseCircleOutline } from "react-icons/io5";
import { GoIssueClosed } from "react-icons/go";
import "./VoiceAssistantConfig.css"
import { useDispatch } from 'react-redux';
import { setInput } from './chatSlice';
import { Tooltip } from 'react-tooltip';
import useFeatures from '../../hooks/useFeatures';

export default function VoiceAgent({ textareaRef, userEmail, onStart, onStop, topic, forceStop }) {
    const dispatch = useDispatch();
    const { canUseVoiceAssistant, hasSubscription, planType, loading: featuresLoading, loadFeatures } = useFeatures();

    const [connected, setConnected] = useState(false);
    const pcRef = useRef(null);
    const dcRef = useRef(null);
    const micStreamRef = useRef(null)
    const [sessionId, setSessionId] = useState(null);
    const sessionIdRef = useRef(null); // Ref для актуального sessionId
    const [errorMessage, setErrorMessage] = useState(null);
    const [toast, setToast] = useState(null);
    const askedQuestionsRef = useRef([]);
    const lastServerRemainingRef = useRef(null);
    const shouldCloseAfterAudioRef = useRef(false);

    // Voice usage tracking
    const [voiceUsage, setVoiceUsage] = useState(null);
    const [displayRemainingMs, setDisplayRemainingMs] = useState(null);
    const [showUsageCounter, setShowUsageCounter] = useState(false);
    const usageIntervalRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    // Загружаем features при монтировании
    useEffect(() => {
        loadFeatures();
    }, [loadFeatures]);

    // Функция загрузки usage
    const fetchVoiceUsage = async () => {
        if (!userEmail) return null;

        try {
            const response = await axiosInstance.get('/voice/usage', {
                headers: {
                    'X-User-Email': userEmail
                }
            });
            setVoiceUsage(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch voice usage:', error);
            return null;
        }
    };

    // Загружаем usage при монтировании и когда подключаемся
    useEffect(() => {
        fetchVoiceUsage();
    }, []);

    // Интервал обновления usage когда подключено
    useEffect(() => {
        if (connected) {
            setShowUsageCounter(true);

            // Сразу загружаем актуальные данные
            // fetchVoiceUsage().then(usage => {
            //     if (usage) {
            //         setDisplayRemainingMs(usage.remainingMs);
            //     }
            // });
            fetchVoiceUsage().then(usage => {
                if (usage) {
                    lastServerRemainingRef.current = Date.now() + usage.remainingMs;
                    setDisplayRemainingMs(usage.remainingMs);
                }
            });

            // Локальный countdown каждую секунду
            // countdownIntervalRef.current = setInterval(() => {
            //     setDisplayRemainingMs(prev => {
            //         if (prev === null || prev <= 0) return 0;
            //         return prev - 1000;
            //     });
            // }, 1000);

            countdownIntervalRef.current = setInterval(() => {
                if (lastServerRemainingRef.current !== null) {
                    const remaining = lastServerRemainingRef.current - Date.now();
                    setDisplayRemainingMs(Math.max(remaining, 0));
                }
            }, 1000);

            // Синхронизация с сервером каждые 30 секунд
            usageIntervalRef.current = setInterval(async () => {
                const usage = await fetchVoiceUsage();
                if (!usage) return;

                const serverExpireAt = Date.now() + usage.remainingMs;

                // вычисляем текущий локальный remaining
                const localRemaining =
                    lastServerRemainingRef.current !== null
                        ? lastServerRemainingRef.current - Date.now()
                        : null;

                // если разница больше 3 секунд — синхронизируем
                if (
                    localRemaining === null ||
                    Math.abs(localRemaining - usage.remainingMs) > 3000
                ) {
                    lastServerRemainingRef.current = serverExpireAt;
                }

                if (usage.expired || usage.remainingMs <= 0) {
                    showToast("Лимит голосового времени исчерпан");
                    stopRelay();
                }
            }, 60000);

            return () => {
                if (usageIntervalRef.current) {
                    clearInterval(usageIntervalRef.current);
                }
                if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                }
            };
            } else {
                if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                }

                fetchVoiceUsage().then(usage => {
                    if (usage) {
                        lastServerRemainingRef.current = Date.now() + usage.remainingMs;
                        setDisplayRemainingMs(usage.remainingMs);
                    }
                });

                setTimeout(() => setShowUsageCounter(false), 5000);
            }
    }, [connected]);

    // Проверка на исчерпание времени
    useEffect(() => {
        if (connected && displayRemainingMs !== null && displayRemainingMs <= 0) {
            showToast("Лимит голосового времени исчерпан");
            stopRelay();
        }
    }, [displayRemainingMs, connected]);

    // Форматирование времени
    const formatTime = (ms) => {
        if (!ms || ms < 0) return '00:00:00';
        const totalSeconds = Math.floor(ms / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    // Получаем цвет в зависимости от оставшегося времени
    const getUsageColor = () => {
        const remaining = displayRemainingMs ?? voiceUsage?.remainingMs;
        if (!remaining) return '#666';
        const remainingMin = remaining / 60000;
        if (remainingMin <= 1) return '#ff4d4f';
        if (remainingMin <= 5) return '#fa8c16';
        if (remainingMin <= 30) return '#faad14';
        return '#52c41a';
    };

    // Принудительная остановка при expired (лимит исчерпан)
    useEffect(() => {
        if (forceStop && connected) {
            //console.log("Force stopping due to expired limit");
            stopRelay();
        }
    }, [forceStop, connected]);

    function showToast(message) {
        setToast(message);
        setTimeout(() => setToast(null), 3000);
    }

    // Короткое название темы для отправки в /voice/reason
    const getTopicName = () => {
        switch (topic) {
            case 'quick_fin':
            case 'accountant_agent_reply':
                return 'бухгалтерским вопросам';

            case 'quick_all':
            case 'law_claim_work':
            case 'law_contract_creation':
            case 'law_corporate_docs_letters':
            case 'law_posts_articles':
            case 'law_agent_reply':
                return 'юридическим вопросам';

            case 'quick':
            case 'actualPracticeM':
            case 'actualPractice':
            case 'letterMfFns':
            case 'tax_agent_reply':
                return 'налоговым вопросам';

            default:
                return 'налогам Российской Федерации';
        }
    };

    // Полная инструкция для OpenAI на русском
    const instruction = (() => {
        const baseContext = `
Ты — голосовой консультант. Твоя задача — помочь пользователю СФОРМУЛИРОВАТЬ вопрос, а НЕ отвечать на него.
Ты работаешь ТОЛЬКО с вопросами по законодательству Российской Федерации.

ВАЖНО:
- Ты НЕ юрист, НЕ бухгалтер, НЕ налоговый консультант
- Ты ТОЛЬКО помогаешь правильно сформулировать вопрос
- НЕ давай советов и рекомендаций по существу
- НЕ отвечай на вопросы по законодательству
- Говори кратко и по делу
- Говори на русском языке

Алгоритм работы:
1. Выслушай пользователя
2. Уточни детали при необхожимости задвая 1-2 уточняющих вопрса
3. Сформулируй чёткий вопрос и озвучь его
4. Спроси подтверждение
`;

        switch (topic) {
            case 'quick_fin':
            case 'accountant_agent_reply':
                return baseContext + `\nТема: БУХГАЛТЕРСКИЙ УЧЁТ в России. Помоги сформулировать вопрос по бухгалтерии.`;

            case 'quick_all':
            case 'law_claim_work':
            case 'law_contract_creation':
            case 'law_corporate_docs_letters':
            case 'law_posts_articles':
            case 'law_agent_reply':
                return baseContext + `\nТема: ЮРИДИЧЕСКИЕ ВОПРОСЫ по законодательству РФ. Помоги сформулировать юридический вопрос.`;

            case 'quick':
            case 'actualPracticeM':
            case 'actualPractice':
            case 'letterMfFns':
            case 'tax_agent_reply':
                return baseContext + `\nТема: НАЛОГИ И НАЛОГООБЛОЖЕНИЕ в России. Помоги сформулировать вопрос по налогам РФ.`;

            default:
                return baseContext + `\nТема: НАЛОГИ Российской Федерации. Помоги сформулировать налоговый вопрос.`;
        }
    })();


    const tools = {
        askSuperviserAgent: async ({ caseDetails }) => {
            if (textareaRef.current) {
                textareaRef.current.value = caseDetails;
                dispatch(setInput(caseDetails));
            }
            // setQuestion(caseDetails)

            // try {
            // const r = await fetch("https://botlegal.tech/supervisor", {
            // method: "POST",
            // headers: { "Content-Type": "application/json" },
            // body: JSON.stringify({ caseDetails }),
            // });

            // if (!r.ok) throw new Error(`Ошибка запроса: ${r.status}`);
            // const data = await r.json();
            // //console.log(data.text)

            // const reply = data?.answer || data?.result || JSON.stringify(data.text);
            // log(`Ответ SupervisorAgent: ${reply}`);
            // setAnswer(reply);

            // return { success: true, caseDetails, reply };
            // } catch (err) {
            //     log("Ошибка при обращении к SupervisorAgent", err);
            //     return { success: false, error: err.message };
            // }
        },
        stopReleyy: async () => {
            setTimeout(() => {
                stopRelay();
            }, 500);
        }

    }

    async function startReplay() {
        // Проверка доступа к голосовому помощнику
        if (!canUseVoiceAssistant) {
            if (!hasSubscription) {
                showToast("Голосовой ассистент доступен только по подписке");
            } else if (planType === 'standard') {
                showToast("Голосовой ассистент недоступен на тарифе Стандарт. Перейдите на Pro");
            } else {
                showToast("Голосовой ассистент доступен на тарифе Pro");
            }
            return;
        }

        try {
            const mic = await navigator.mediaDevices.getUserMedia({ audio: true })
            micStreamRef.current = mic;

            const pc = new RTCPeerConnection()
            pcRef.current = pc;

            // //console.log(instraction)

            mic.getTracks().forEach((track) => pc.addTrack(track, mic))

            const audioEl = document.createElement('audio')
            audioEl.autoplay = true;
            pc.ontrack = (ev) => {
                audioEl.srcObject = ev.streams[0];
            }

            const dc = pc.createDataChannel("oai-events")
            dcRef.current = dc;

            dc.onopen = () => {
                const topicName = getTopicName();

                // Настраиваем сессию с function calling
                const sessionUpdate = {
                    type: "session.update",
                    session: {
                        voice: "alloy",
                        instructions: `
Ты — голосовой консультант по ${topicName} в Российской Федерации.
Твоя ЕДИНСТВЕННАЯ задача — помочь пользователю СФОРМУЛИРОВАТЬ вопрос.

КРИТИЧЕСКИ ВАЖНО:
- Ты НЕ отвечаешь на вопросы по существу
- Ты ТОЛЬКО помогаешь сформулировать вопрос
- Работаешь ТОЛЬКО с законодательством России
- Говори ТОЛЬКО на русском языке
- Говори кратко

АЛГОРИТМ:
1. Выслушай пользователя
2. Уточни детали при необхожимости задвая 1-2 уточняющих вопрса
3. Когда понял суть — ОБЯЗАТЕЛЬНО вызови функцию "formulateQuestion" с текстом пользователя
4. Озвучь результат и попроси подтвердить

ВСЕГДА вызывай функцию "formulateQuestion" когда пользователь описал свою ситуацию!
Если пользователь подтверждает, что вопрос сформулирован правильно —
НЕ отвечай текстом.
СРАЗУ вызови функцию "confirmAndClose" и больше ничего не говори.

Если пользователь хочет изменить формулировку —
продолжай диалог и НЕ вызывай confirmAndClose.
`,
                        modalities: ["audio", "text"],
                        input_audio_transcription: {
                            model: "whisper-1"
                        },
                        turn_detection: {
                            type: "server_vad",
                            threshold: 0.5,
                            prefix_padding_ms: 300,
                            silence_duration_ms: 700
                        },
                        tools: [
                            {
                                type: "function",
                                name: "formulateQuestion",
                                description: "Вызови эту функцию чтобы сформулировать вопрос пользователя. Передай описание ситуации от пользователя. Функция вернёт корректно сформулированный вопрос.",
                                parameters: {
                                    type: "object",
                                    properties: {
                                        userDescription: {
                                            type: "string",
                                            description: "Описание ситуации пользователя своими словами"
                                        }
                                    },
                                    required: ["userDescription"]
                                }
                            },
                            {
                                type: "function",
                                name: "confirmAndClose",
                                description: "Вызови эту функцию если пользователь подтвердил, что вопрос сформулирован правильно",
                                parameters: {
                                    type: "object",
                                    properties: {}
                                }
                            }
                        ],
                        tool_choice: "auto"
                    },
                };

                dc.send(JSON.stringify(sessionUpdate));

                // Приветствие
                dc.send(JSON.stringify({
                    type: "response.create",
                    response: {
                        modalities: ["audio", "text"],
                        instructions: `Скажи кратко на русском: "Здравствуйте! Я помогу сформулировать вопрос по ${topicName}. Расскажите вашу ситуацию."`
                    }
                }));
            }

            dc.onmessage = async (ev) => {
                const msg = JSON.parse(ev.data);
                //console.log("WebRTC msg:", msg.type, msg);

                // OpenAI вызвал нашу функцию
                if (msg.type === "response.function_call_arguments.done") {
                    //console.log("Function called:", msg.name, msg.arguments);

                    if (msg.name === "formulateQuestion") {
                        try {
                            const args = JSON.parse(msg.arguments);
                            const userDescription = args.userDescription;

                            //console.log("Sending to reasoning:", userDescription);

                            // Вызываем наш reasoning агент
                            const result = await fetch(
                                `${process.env.REACT_APP_API_URL}/voice/reason`,
                                {
                                    method: "POST",
                                    headers: { "Content-Type": "application/json" },
                                    body: JSON.stringify({
                                        text: userDescription,
                                        topic: getTopicName()
                                    })
                                }
                            ).then(r => r.json());

                            //console.log("Reasoning result:", result);

                            const finalQuestion = result.finalText || "Не удалось сформулировать вопрос";

                            // Вставляем в textarea
                            if (textareaRef.current) {
                                textareaRef.current.value = finalQuestion;
                                textareaRef.current.dispatchEvent(new Event("input", { bubbles: true }));
                                dispatch(setInput(finalQuestion));
                            }

                            // Отправляем результат функции обратно в OpenAI
                            dc.send(JSON.stringify({
                                type: "conversation.item.create",
                                item: {
                                    type: "function_call_output",
                                    call_id: msg.call_id,
                                    output: JSON.stringify({ formulated_question: finalQuestion })
                                }
                            }));

                            // Просим озвучить результат
                            dc.send(JSON.stringify({
                                type: "response.create",
                                response: {
                                    modalities: ["audio", "text"],
                                    instructions: `Скажи кратко на русском: "Вопрос сформулирован. Можете отправить его или уточнить детали."`
                                }
                            }));

                            // const usage = await fetchVoiceUsage();
                            // if (usage) {
                            //     setDisplayRemainingMs(usage.remainingMs);
                            // }

                            showToast("Вопрос сформулирован!");
                            

                        } catch (err) {
                            console.error("Reasoning error:", err);

                            dc.send(JSON.stringify({
                                type: "conversation.item.create",
                                item: {
                                    type: "function_call_output",
                                    call_id: msg.call_id,
                                    output: JSON.stringify({ error: "Ошибка обработки" })
                                }
                            }));

                            dc.send(JSON.stringify({
                                type: "response.create",
                                response: {
                                    modalities: ["audio"],
                                    instructions: "Скажи: Произошла ошибка. Попробуйте ещё раз."
                                }
                            }));
                        }
                    }

                    if (msg.name === "confirmAndClose") {
                        //console.log("User confirmed. Will close after audio.");

                        // 1️⃣ ОБЯЗАТЕЛЬНО ответить на function_call
                        dc.send(JSON.stringify({
                            type: "conversation.item.create",
                            item: {
                                type: "function_call_output",
                                call_id: msg.call_id,
                                output: JSON.stringify({ success: true })
                            }
                        }));

                        // 2️⃣ Попросить ассистента сказать финальную фразу
                        dc.send(JSON.stringify({
                            type: "response.create",
                            response: {
                                modalities: ["audio"],
                                instructions: "Скажи кратко: Спасибо. Вопрос сформирован. Завершаю сессию."
                            }
                        }));

                        // 3️⃣ После аудио закроем
                        shouldCloseAfterAudioRef.current = true;
                    }
                }

                // Логируем транскрипцию для отладки
                if (msg.type === "conversation.item.input_audio_transcription.completed") {
                    //console.log("User said:", msg.transcript);
                }

                if (msg.type === "response.output_audio.done") {
                    if (shouldCloseAfterAudioRef.current) {
                        shouldCloseAfterAudioRef.current = false;
                        stopRelay();
                    }
                }
            };


            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);

            // const res = await axiosInstance.post("/voice/client-offer", offer.sdp)
            const res = await fetch(`${process.env.REACT_APP_API_URL}/voice/client-offer`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/sdp",
                    "X-User-Email": userEmail,
                },
                body: offer.sdp,
            });

            if (!res.ok) {
                let msg = "Не удалось подключиться к голосовому ассистенту";

                try {
                    const data = await res.json();
                    msg = data.error || data.message || msg;
                } catch { }

                showToast(msg);
                return;
            }



            const newSessionId = res.headers.get("x-session-id")
            const isVoiceAssiatant = res.headers.get("x-is-voice-assistant")
            //console.log("sessionId, ", newSessionId)
            setSessionId(newSessionId)
            sessionIdRef.current = newSessionId; // Сохраняем в ref

            //console.log("vooice ", isVoiceAssiatant)

            //console.log(res)

            const answer = await res.text()

            await pc.setRemoteDescription({ type: "answer", sdp: answer })

            setConnected(true);
            onStart?.();

        } catch {
            // error
        }
    }

    async function stopRelay() {
        const currentSessionId = sessionIdRef.current; // Берём из ref
        //console.log("stopRelay called, sessionId:", currentSessionId)
        setConnected(false);
        onStop?.();


        try {
            if (currentSessionId) {
                await fetch(`${process.env.REACT_APP_API_URL}/voice/stop-session`, {
                    method: "POST",
                    headers: {
                        "X-User-Email": userEmail,
                        "X-Session-Id": currentSessionId,
                    }
                });
            }
            if (dcRef.current) dcRef.current.close();
            if (pcRef.current) pcRef.current.close();
            if (micStreamRef.current) {
                micStreamRef.current.getTracks().forEach((track) => track.stop())
            }
            pcRef.current = null;
            dcRef.current = null;
            micStreamRef.current = null;
            sessionIdRef.current = null; // Очищаем ref

            lastServerRemainingRef.current = null;

        } catch {
            // error
        }
    }

    // function notUse() {
    //     // setQuestion(null)
    //     if (textareaRef.current) {
    //             textareaRef.current.value = null; // вставляем текст
    //             textareaRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    //     }
    // }

    return (
        <div className="voice-agent">
            {toast && (
                <div className="voice-toast">
                    {toast}
                </div>
            )}

            {/* Voice Usage Counter */}
            {connected && displayRemainingMs !== null && (
                <div className="voice-usage-counter">
                    <div
                        className="voice-usage-indicator"
                        style={{ background: getUsageColor(), color: getUsageColor() }}
                    />
                    <div className="voice-usage-time" style={{ color: getUsageColor() }}>
                        {formatTime(displayRemainingMs)}
                    </div>
                </div>
            )}

            {connected ? (
                <div className="voice-agent__connected">
                    <IoCloseCircleOutline className="voice-agent__icon"
                        onClick={() => {
                            stopRelay();
                            // notUse();
                        }}
                        data-tooltip-id="voice-tooltip"
                        data-tooltip-content="отклонить"
                        data-tooltip-place="top"

                    />
                    <GoIssueClosed className="voice-agent__icon"
                        onClick={stopRelay}
                        data-tooltip-id="voice-tooltip"
                        data-tooltip-content="принять"
                        data-tooltip-place="top"
                    />
                </div>
            ) : (
                <RiVoiceAiLine
                    className={`voice-agent__icon--main ${!canUseVoiceAssistant ? 'voice-agent__icon--disabled' : ''}`}
                    onClick={startReplay}
                    data-tooltip-id="voice-tooltip"
                    data-tooltip-content={
                        !canUseVoiceAssistant
                            ? (!hasSubscription
                                ? "Голосовой ассистент доступен по подписке"
                                : planType === 'standard'
                                    ? "Недоступно на тарифе Стандарт"
                                    : "Доступно на тарифе Pro")
                            : "Голосовой Ассистент"
                    }
                    data-tooltip-place="top"
                />
            )}
            <Tooltip
                id="voice-tooltip"
                place="right"
                style={{
                    backgroundColor: "#222",   // background
                    color: "#fff",              // text color
                    fontSize: "14px",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                    zIndex: 9999,
                }} />
        </div>
    );
}